import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { format, parseISO } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  useCreateDirectOrder,
  useCreateQuotation,
  useCustomer,
  useEnquiry,
  useHalls,
  useOrder,
  useOutlets,
  usePackages,
  usePaxLimits,
  useHallSlotBoard,
  useEventTypes,
  useQuotation,
  useReviseOrder,
  useReviseQuotation,
  useServiceTypes,
  useSessionTypes,
  usePreviewQuotation,
} from '@/api/hooks'
import { BLANK_DELIVERY_ADDRESS, DeliveryAddressFields } from '@/components/DeliveryAddressFields'
import type {
  DeliveryAddress,
  CateringPackage,
  CustomerSummary,
  Hall,
  MenuItem as MenuItemType,
  OrderDetail,
  PackageItem,
  QuotationPreview,
} from '@/api/types'
import { BillLines, type BillLine } from '@/components/BillLines'
import { DishPicker, type Dish } from '@/pages/catalogue/DishPicker'
import { api } from '@/api/client'
import { useAuthStore } from '@/auth/authStore'
import { CustomerPicker } from '@/components/CustomerPicker'
import { DiscountField } from '@/components/DiscountField'
import { NewCustomerDialog } from '@/pages/CustomersPage'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import {
  ErrorBanner,
  formatDate,
  formatMoney,
  formatSlotHours,
  humanise,
  InlineSpinner,
  isoDate,
  newId,
  PageHeader,
  TimeField,
} from '@/components/common'

/**
 * One sitting a day can be split into, with the hour it usually starts.
 *
 * <p>Read from the sittings master for the line of business being sold. Four of
 * these were written in here and offered to every service alike, so an office
 * selling a tiffin round or nothing but lunch could not say so.</p>
 */
type Sitting = { key: string; label: string; start: string; chargedByPax: boolean }

const STEPS = ['Customer and event', 'Sessions and pax', 'Package and menu', 'Charges and terms']

/**
 * A line charged on top of a sitting, priced as quantity times rate.
 *
 * <p>Belongs to one sitting: a live counter is at the reception, not at the
 * lunch the next day. Picked off the catalogue rather than typed, so the same
 * counter is the same item on every quotation and the kitchen recognises what
 * it has been asked for.</p>
 */
/**
 * A line charged against one sitting: a dish, or a package sold as a lot.
 *
 * <p>The shared bill line, plus the sitting it belongs to - a live counter is
 * at the reception, not at the next day's lunch, and the kitchen has to know
 * which is which.</p>
 */
type ExtraItem = BillLine & { sessionKey: string }

type SessionDraft = {
  key: string
  mealPeriod: string
  /**
   * What the master calls this sitting, and where it comes in the day. Carried
   * on the draft rather than looked up again, so a sitting withdrawn from the
   * master half-way through a quotation still reads as itself.
   */
  mealLabel: string
  mealOrder: number
  /**
   * Whether this sitting is priced by the head. False and it is quoted as
   * lines of quantity times rate: no pax, nothing plated, so neither the head
   * count nor the service style is asked for.
   */
  chargedByPax: boolean
  sessionName: string
  sessionDate: string
  startTime: string
  hallId: string
  hallSlotIds: string[]
  serviceStyle: string
  hallRentalAmount: string
  pax: string
  packageId: string
  /** Menu item ids chosen per selection rule. */
  selections: Record<string, string[]>
  /**
   * A note against one chosen dish, keyed `packageItemId:menuItemId`. Only a
   * dish that was actually picked can carry one.
   */
  menuRemarks: Record<string, string>
  /**
   * Dishes to be cooked in front of the guests, keyed `packageItemId:menuItemId`
   * like the portions. Only dishes the catalogue marks as live-capable can be
   * in here, and choosing one is what puts a counter and its staff on the job.
   */
  liveCooking: Record<string, boolean>
  /**
   * The serving fraction the package sets for each choice, keyed
   * `packageItemId:menuItemId` — the same dish can be a whole serving under one
   * item and a quarter under another. Read from the package when its rules
   * load, and sent on as the quantity the kitchen has to produce per guest.
   */
  portions: Record<string, number>
  /** Chosen when no package is used. */
  /**
   * The order session's own id. Present only when revising an order that
   * already has this sitting; absent for a quotation, for one being created,
   * and for one being added here for the first time - which is exactly how
   * the revise endpoint tells "move this one" from "this is new".
   */
  id?: string
  /**
   * When this sitting ends. Only revising an order needs it - a quotation's
   * sittings run the meal's usual length and never ask; an order's session
   * endpoints take an explicit end time the same as they take a start.
   */
  endTime?: string
  /**
   * Set when revising an order whose sitting has dishes on it already but no
   * package this screen can check them against - which, converted from a
   * quotation, is most sittings: the dishes came across, the link to the
   * rule each one was chosen under did not. Sending a menu for the sitting
   * anyway would read as "this is the whole menu now" and drop whatever this
   * screen could not place, so nothing here is sent and the dishes stay
   * editable only where they always were, on the order's own menu list.
   */
  menuUnmappable?: boolean
  /**
   * What is actually on the sitting, shown next to a package box that would
   * otherwise look empty for no reason a person reading it could tell. Set
   * whenever {@link menuUnmappable} is - read-only, because nothing here
   * is what is being saved.
   */
  existingDishes?: string[]
  /**
   * The same dishes as {@link existingDishes}, kept by id rather than by
   * name. A package picked here to replace one this screen could not check
   * against starts from a blank slate otherwise - this is what lets it start
   * from what is actually cooking instead, wherever the new package's own
   * rules recognise a dish already on the sitting.
   */
  existingMenuItemIds?: { menuItemId: string; liveCooking: boolean }[]
  /**
   * This sitting's package is being customized for this order alone: a dish
   * not offered on the package can be chosen, a mandatory dish can be left
   * off, and the rate is agreed apart from what the package would otherwise
   * charge. Nothing here is written back to the package - only this sitting.
   */
  customizePackage?: boolean
  /** The rate per head agreed for a customized package; blank keeps the package's own. */
  customRate?: string
  /** Dishes added to this sitting's package beyond its own choices, by rule. */
  customItems?: Record<string, Dish[]>
  /**
   * What this sitting is actually priced at on the order right now, read
   * back only when revising one. Not sent on save - the revise endpoint
   * already keeps a session's own rate unless it is genuinely changed -
   * but handed to the preview so the price panel shows what the order
   * will still read, not what the package costs off today's catalogue.
   */
  knownRate?: string
}

function newSession(
  meal: Sitting,
  order: number,
  date: string,
  hallId: string,
  hallSlotIds: string[] = [],
  startTime?: string,
  hallRentalAmount = '',
): SessionDraft {
  return {
    key: `${meal.key}-${date}`,
    mealPeriod: meal.key,
    mealLabel: meal.label,
    mealOrder: order,
    chargedByPax: meal.chargedByPax,
    sessionName: '',
    sessionDate: date,
    // The hall and its slots are chosen once for the event, so the sitting
    // takes them rather than asking again for every meal of the same day.
    startTime: startTime || meal.start,
    hallId,
    hallSlotIds,
    // Leaf is how most of this trade actually serves; a buffet is the exception
    // and is chosen deliberately.
    serviceStyle: 'LEAF',
    hallRentalAmount,
    pax: '',
    packageId: '',
    selections: {},
    menuRemarks: {},
    liveCooking: {},
    portions: {},
  }
}

/**
 * Builds a quotation the way the sale is actually made: who and when, then which
 * sittings and how many at each, then what each sitting eats, then the money.
 */
/**
 * The same wizard raises a quotation or takes an order outright.
 *
 * <p>A regular customer rings up and books; nobody wants a quotation approved,
 * emailed and accepted first. What the order still needs is everything this
 * screen works out - the sittings, the menu, the hall rental, the tax - so the
 * only thing that changes at the end is which document is created.</p>
 */
export function QuotationBuilderPage({ asOrder = false }: { asOrder?: boolean } = {}) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const enquiryId = params.get('enquiryId') ?? undefined

  // Present only on /quotations/:editId/edit - the same wizard, loaded with
  // what is on the current version and saved as a revision rather than a new
  // quotation.
  const { editId } = useParams<{ editId?: string }>()
  const { data: editingQuotation } = useQuotation(editId)
  const revise = useReviseQuotation()

  // Present only on /orders/:orderEditId/revise - the same wizard again, this
  // time loaded from a confirmed order and saved past the change-request
  // desk rather than through it.
  const { orderEditId } = useParams<{ orderEditId?: string }>()
  const { data: editingOrder } = useOrder(orderEditId)
  const reviseOrder = useReviseOrder()

  const { data: enquiry } = useEnquiry(enquiryId)
  const { data: outlets } = useOutlets()

  const [step, setStep] = useState(0)

  // ------------------------------------------------------------- event step
  // Whoever was picked is held whole, not as an id looked up again in a list of
  // the first twenty customers. That list is not the one the picker searches,
  // so anybody further down it - Sangeetha, CUS/00073 - was chosen and then
  // shown as nobody, and the form would not go on.
  const [customer, setCustomer] = useState<CustomerSummary | null>(null)
  const customerId = customer?.id ?? ''
  // An enquiry names its customer by id alone, so they are fetched to fill the
  // box with the name the enquiry was taken under.
  const { data: enquiryCustomer } = useCustomer(enquiry?.customerId)
  useEffect(() => {
    if (!enquiryCustomer) return
    // Whoever is already in the box was put there on purpose; the enquiry only
    // supplies a starting point.
    setCustomer((current) => current ?? enquiryCustomer)
  }, [enquiryCustomer])
  // A revision names its customer by id too, the same as an enquiry does.
  const { data: editCustomer } = useCustomer(editingQuotation?.customerId)
  useEffect(() => {
    if (!editCustomer) return
    setCustomer((current) => current ?? editCustomer)
  }, [editCustomer])
  const { data: editOrderCustomer } = useCustomer(editingOrder?.customerId)
  useEffect(() => {
    if (!editOrderCustomer) return
    setCustomer((current) => current ?? editOrderCustomer)
  }, [editOrderCustomer])
  const [outletId, setOutletId] = useState('')
  const [serviceType, setServiceType] = useState('BANQUET')
  const [eventType, setEventType] = useState('WEDDING')
  const [eventDate, setEventDate] = useState(() => isoDate(0))
  const [hallId, setHallId] = useState('')
  // What may be sold for this job: the branch it is being taken at, the hall it
  // is booked in, and the line of business. A package written against another
  // branch or another hall is not on offer here, and offering it was how a
  // reception at one hall was quoted on another hall's menu.
  const { data: packages } = usePackages({
    serviceType: serviceType || undefined,
    outletId: outletId || undefined,
    hallId: hallId || undefined,
  })
  /** The slots this event holds, chosen once with the hall. */
  const [hallSlotIds, setHallSlotIds] = useState<string[]>([])
  const [slotStartTime, setSlotStartTime] = useState('')
  const [slotRental, setSlotRental] = useState('')
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [address, setAddress] = useState<DeliveryAddress>(BLANK_DELIVERY_ADDRESS)
  // How far the job is and the pin that finds it. Both matter only away from
  // our own halls: the distance decides the transport and how early the vans
  // leave, and a written address down a Chennai lane will not find the place.
  const [venueDistanceKm, setVenueDistanceKm] = useState('')
  const [venueMapUrl, setVenueMapUrl] = useState('')
  // Whether we deliver or they collect, and whether we send people. Both are
  // outdoor questions: a banquet is served by us, in our own hall.
  const [fulfilmentMode, setFulfilmentMode] = useState('DELIVERY')
  const [manpowerRequired, setManpowerRequired] = useState(true)

  /**
   * Where the food is served decides half of this screen.
   *
   * <p>A banquet fills a hall and a restaurant party fills a dining room: both
   * happen on our own premises, are booked into a space and a slot, and have no
   * address to deliver to. Outdoor catering and bulk takeaway go out of the
   * door, so they need an address and no space at all.</p>
   */
  // What the chosen branch actually sells, and what the chosen service needs.
  const { data: serviceTypes } = useServiceTypes({ outletId: outletId || undefined })
  const bookable = (serviceTypes ?? []).filter((type) => type.bookable)
  const service = bookable.find((type) => type.code === serviceType)
  // Which occasions this line of business is sold for, and so whether it is
  // asked at all: none mapped, no question. Read from the master rather than
  // from a flag, so the question is only put where there are answers to it.
  const { data: occasions } = useEventTypes({ serviceType: serviceType || undefined })
  const eventTypeRequired = (occasions ?? []).length > 0
  // The sittings this line of business is sold by, off the master. One list
  // for the whole company, narrowed to what this service is assigned - and an
  // empty answer is the answer: a service with none is not asked which meal.
  const { data: sessionTypes } = useSessionTypes({ serviceType: serviceType || undefined })
  const sittings: Sitting[] = useMemo(
    () =>
      (sessionTypes ?? []).map((type) => ({
        key: type.code,
        label: type.name,
        start: (type.startTime ?? '').slice(0, 5),
        chargedByPax: type.chargedByPax,
      })),
    [sessionTypes],
  )

  // Moving to an outlet that does not sell what was chosen leaves the field
  // empty and the step quietly invalid, so it falls to the first thing that
  // outlet does sell rather than to nothing.
  useEffect(() => {
    if (bookable.length > 0 && !bookable.some((type) => type.code === serviceType)) {
      setServiceType(bookable[0].code)
    }
  }, [bookable, serviceType])
  const onPremises = Boolean(service?.spaceType)
  const spaceType = (service?.spaceType === 'DINING' ? 'DINING' : 'HALL') as 'HALL' | 'DINING'
  const needsAddress = service?.needsDeliveryAddress ?? false
  /**
   * Enough of an address to find the place: a street or an area at the
   * least. Checked here rather than left to the server, because a job
   * walked through every step only to be refused on the way out is a job
   * sent back to redo all of it instead of the one field it was missing.
   */
  const somewhereToGo =
    Boolean(address.streetName?.trim())
    || Boolean(address.areaName?.trim())
    || Boolean(address.buildingName?.trim())
  const addressMissing = needsAddress && !onPremises && !somewhereToGo

  // Only the spaces the service being sold is served in.
  const { data: halls } = useHalls(outletId || undefined, onPremises ? spaceType : undefined)

  /**
   * How many this job may be taken for at this branch, narrowed by the hall.
   *
   * <p>The branch's own answer where it has one, the business's otherwise, and
   * the room's ceiling on top of it. Asked here so the head count boxes can say
   * it while it is being typed rather than after Continue.</p>
   */
  const { data: paxLimits } = usePaxLimits(serviceType || undefined, outletId || undefined)
  const bookedHall = (halls ?? []).find((one) => one.id === hallId)
  const hallCeiling = bookedHall
    ? bookedHall.maxPax ?? (bookedHall.seatingCapacity > 0 ? bookedHall.seatingCapacity : null)
    : null
  const paxCeilings = [paxLimits?.maxPax, hallCeiling].filter(
    (one): one is number => typeof one === 'number' && one > 0,
  )
  const paxFloor = Math.max(paxLimits?.minPax ?? 0, bookedHall?.minPax ?? 0) || null
  const paxCeiling = paxCeilings.length > 0 ? Math.min(...paxCeilings) : null

  // ----------------------------------------------------------- session step
  const [sessions, setSessions] = useState<SessionDraft[]>([])

  // ----------------------------------------------------------- charges step
  const [discountMode, setDiscountMode] = useState<'PERCENT' | 'AMOUNT'>('PERCENT')
  const [discountValue, setDiscountValue] = useState('')
  const [discountRecommendedBy, setDiscountRecommendedBy] = useState('')
  const [discountReason, setDiscountReason] = useState('')
  const [validUntil, setValidUntil] = useState(() => isoDate(15))
  const [paymentTerms, setPaymentTerms] = useState('')
  const [notes, setNotes] = useState('')
  /** Who placed it, kept on the order taken over the counter. */
  const [orderedByName, setOrderedByName] = useState('')
  // Old data, entered as it stood - never for an order taken today, which
  // keeps its place in this company's own run of numbers. Admin only.
  const [orderNumber, setOrderNumber] = useState('')
  const hasRole = useAuthStore((state) => state.hasRole)
  const isAdmin = hasRole('SUPER_ADMIN') || hasRole('MANAGEMENT')
  // Items charged on top of the sittings - a live counter, chairs hired in.
  // Nothing about them depends on a package being used.
  const [extraItems, setExtraItems] = useState<ExtraItem[]>([])

  const previewQuote = usePreviewQuotation()
  const create = useCreateQuotation()
  const createOrder = useCreateDirectOrder()
  const [quote, setQuote] = useState<QuotationPreview | null>(null)

  useEffect(() => {
    if (!enquiry) return
    if (enquiry.outletId) setOutletId(enquiry.outletId)
    setServiceType(enquiry.serviceType)
    setEventType(enquiry.eventType)
    setEventDate(enquiry.eventDate)
    if (enquiry.preferredHallId) setHallId(enquiry.preferredHallId)
    if (enquiry.deliveryAddress) setDeliveryAddress(enquiry.deliveryAddress)
    // Taken once when the customer rang; not typed again here.
    if (enquiry.address) setAddress({ ...BLANK_DELIVERY_ADDRESS, ...enquiry.address })
  }, [enquiry])

  // Loaded once and only once: after this, everything on screen is what
  // somebody typed, and a background refetch must not walk back over it.
  const hydratedHeaderRef = useRef(false)
  useEffect(() => {
    const v = editingQuotation?.currentVersion
    if (!v || hydratedHeaderRef.current) return
    hydratedHeaderRef.current = true

    setOutletId(editingQuotation.outletId)
    setServiceType(v.serviceType)
    setEventType(v.eventType ?? '')
    setEventDate(v.eventDate)
    setHallId(v.hallId ?? '')
    setDeliveryAddress(v.deliveryAddress ?? '')
    setAddress(v.address ? { ...BLANK_DELIVERY_ADDRESS, ...v.address } : BLANK_DELIVERY_ADDRESS)
    setVenueDistanceKm(v.venueDistanceKm != null ? String(v.venueDistanceKm) : '')
    setVenueMapUrl(v.venueMapUrl ?? '')
    setFulfilmentMode(v.fulfilmentMode ?? 'DELIVERY')
    setManpowerRequired(v.manpowerRequired ?? true)

    // The hall and its slots are one choice for the whole event; every
    // session on a banquet quotation was booked into the same one.
    const firstOnPremises = v.sessions.find((s) => s.hallId)
    if (firstOnPremises) {
      setHallSlotIds(firstOnPremises.hallSlotIds ?? [])
      setSlotStartTime(firstOnPremises.startTime?.slice(0, 5) ?? '')
      setSlotRental(
        firstOnPremises.hallRentalAmount ? String(firstOnPremises.hallRentalAmount) : '',
      )
    }

    const flatRate = v.discountAmount > 0 && v.discountPercent <= 0
    setDiscountMode(flatRate ? 'AMOUNT' : 'PERCENT')
    setDiscountValue(
      flatRate
        ? String(v.discountAmount)
        : v.discountPercent > 0
          ? String(v.discountPercent)
          : '',
    )
    setDiscountRecommendedBy(v.discountRecommendedBy ?? '')
    setDiscountReason(v.discountReason ?? '')
    setValidUntil(v.validUntil ?? isoDate(15))
    setPaymentTerms(v.paymentTerms ?? '')
    setNotes(v.notes ?? '')
  }, [editingQuotation])

  // Waits on the sittings master before turning the saved sessions into
  // drafts: a session's label and whether it is priced by the head both come
  // from there, not from anything stored on the quotation itself.
  const hydratedSessionsRef = useRef(false)
  useEffect(() => {
    const v = editingQuotation?.currentVersion
    if (!v || sessionTypes === undefined || hydratedSessionsRef.current) return
    if (v.sessions.length === 0) return
    // sessionTypes can already be sitting in the cache for whatever service
    // type this screen last showed - the header's own hydration effect has
    // not necessarily landed in a render yet, and reading it before it has
    // would build every session's label and pax rule off the wrong sittings.
    if (serviceType !== v.serviceType) return
    hydratedSessionsRef.current = true

    let cancelled = false
    void Promise.all(
      v.sessions.map(async (s): Promise<SessionDraft> => {
        const meal = sittings.find((one) => one.key === s.mealPeriod)
        const selections: Record<string, string[]> = {}
        const portions: Record<string, number> = {}
        const liveCooking: Record<string, boolean> = {}
        const menuRemarks: Record<string, string> = {}
        const customItems: Record<string, Dish[]> = {}
        let customizePackage = false

        // The package's own choices, so a dish recorded against an item but
        // not actually one of its choices - put there deliberately, when the
        // sitting was customized, whether under one of the package's own
        // items or a whole new one added beyond them - is shown back where
        // it was put rather than alongside dishes the package offers.
        let offered = new Set<string>()
        let rulesLoaded = false
        if (s.packageId) {
          try {
            const rules = await api.get<PackageItem[]>(
              `/api/v1/packages/${s.packageId}/selection-rules`,
            )
            offered = new Set(rules.flatMap((rule) => rule.choices.map((c) => c.menuItemId)))
            rulesLoaded = true
          } catch {
            // Read again once the rules are reachable; until then every
            // selection is kept exactly as recorded, as a normal choice.
          }
        }

        for (const selection of s.menuSelections) {
          if (!selection.packageItemId) continue
          const itemKey = `${selection.packageItemId}:${selection.menuItemId}`
          if (!rulesLoaded || offered.has(selection.menuItemId)) {
            selections[selection.packageItemId] = [
              ...(selections[selection.packageItemId] ?? []),
              selection.menuItemId,
            ]
            portions[itemKey] = selection.quantity
            if (selection.liveCooking) liveCooking[itemKey] = true
            if (selection.notes) menuRemarks[itemKey] = selection.notes
            continue
          }
          customItems[selection.packageItemId] = [
            ...(customItems[selection.packageItemId] ?? []),
            { id: selection.menuItemId, itemCode: '', name: selection.itemName },
          ]
          customizePackage = true
        }

        return {
          key: `${s.mealPeriod}-${s.sessionDate}`,
          mealPeriod: s.mealPeriod,
          mealLabel: meal?.label ?? humanise(s.mealPeriod),
          mealOrder: meal ? sittings.indexOf(meal) : s.sequenceNo,
          chargedByPax: meal?.chargedByPax ?? s.adultPax > 0,
          sessionName: s.sessionName ?? '',
          sessionDate: s.sessionDate,
          startTime: s.startTime?.slice(0, 5) ?? meal?.start ?? '',
          hallId: s.hallId ?? '',
          hallSlotIds: s.hallSlotIds ?? [],
          serviceStyle: s.serviceStyle || 'LEAF',
          hallRentalAmount: s.hallRentalAmount ? String(s.hallRentalAmount) : '',
          pax: s.adultPax ? String(s.adultPax) : '',
          packageId: s.packageId ?? '',
          selections,
          menuRemarks,
          liveCooking,
          portions,
          customizePackage,
          customItems,
          customRate: customizePackage && s.pricePerPerson ? String(s.pricePerPerson) : undefined,
        }
      }),
    ).then((draftSessions) => {
      if (cancelled) return
      setSessions(draftSessions)
    })

    // Extras are kept at the version level, each tagged with the session it
    // belongs to - the same lines a bill would show, read back into the rows
    // the counter builds them from.
    const draftExtras: ExtraItem[] = []
    for (const s of v.sessions) {
      const sessionKey = `${s.mealPeriod}-${s.sessionDate}`
      for (const line of v.lines) {
        if (line.quotationSessionId !== s.id) continue
        // HALL_RENTAL and, on a session that carries its own package, PACKAGE
        // too are not lines anybody typed in - the pricing engine writes them
        // straight from the session's own hallRentalAmount and packageId every
        // time the version is built. Resending them as if they were counter
        // entries would charge for the package twice over.
        if (line.lineType === 'HALL_RENTAL') continue
        if (line.lineType === 'PACKAGE' && s.packageId) continue
        if (line.lineType !== 'EXTRA_MENU_ITEM' && line.lineType !== 'PACKAGE') continue
        const isPackageLine = line.lineType === 'PACKAGE'
        draftExtras.push({
          key: line.id,
          kind: isPackageLine ? 'PACKAGE' : 'ITEM',
          packageId: isPackageLine ? line.referenceId ?? '' : '',
          menuItemId: isPackageLine ? '' : line.referenceId ?? '',
          item: null,
          description: line.description,
          quantity: String(line.quantity),
          unit: line.unit,
          unitPrice: String(line.unitPrice),
          taxRatePercent: String(line.taxRatePercent),
          sessionKey,
        })
      }
    }
    setExtraItems(draftExtras)
    return () => {
      cancelled = true
    }
  }, [editingQuotation, sessionTypes, sittings])

  // The order side of the same hydration. An order has no top-level hall or
  // address at all - every one of those lives on its sessions - so the first
  // one that actually booked a hall stands in for "the event's" hall, the
  // same fiction the quotation side of this effect already carries for an
  // outdoor job's address.
  const hydratedOrderHeaderRef = useRef(false)
  useEffect(() => {
    if (!editingOrder || hydratedOrderHeaderRef.current) return
    hydratedOrderHeaderRef.current = true

    setOutletId(editingOrder.outletId)
    setServiceType(editingOrder.serviceType)
    setEventType(editingOrder.eventType ?? '')
    setEventDate(editingOrder.eventDate)
    setFulfilmentMode(editingOrder.fulfilmentMode ?? 'DELIVERY')
    setManpowerRequired(editingOrder.manpowerRequired ?? true)

    const withHall = editingOrder.sessions.find((s) => s.hallId)
    const first = withHall ?? editingOrder.sessions[0]
    if (first) {
      setHallId(first.hallId ?? '')
      setHallSlotIds(first.hallSlotIds ?? [])
      setSlotStartTime(format(parseISO(first.eventStart), 'HH:mm'))
      setDeliveryAddress(first.deliveryAddress ?? '')
      setAddress(first.address ? { ...BLANK_DELIVERY_ADDRESS, ...first.address } : BLANK_DELIVERY_ADDRESS)
      setVenueDistanceKm(first.venueDistanceKm != null ? String(first.venueDistanceKm) : '')
      setVenueMapUrl(first.venueMapUrl ?? '')
    }

    // An order carries only the figure a discount came to, never the rate it
    // was struck at - unlike a quotation, which keeps both.
    setDiscountMode('AMOUNT')
    setDiscountValue(editingOrder.discountAmount ? String(editingOrder.discountAmount) : '')
    setDiscountRecommendedBy(editingOrder.discountRecommendedBy ?? '')
    setDiscountReason(editingOrder.discountReason ?? '')
    setNotes(editingOrder.notes ?? '')
  }, [editingOrder])

  // Waits on the sittings master exactly as the quotation side does, and on
  // top of that, on a package's own selection rules - fetched here rather
  // than left to the menu step, because a dish already on the sitting has to
  // be matched to the rule it belongs to before anything can be shown as
  // chosen rather than as a blank package nobody has touched yet.
  const hydratedOrderSessionsRef = useRef(false)
  useEffect(() => {
    if (!editingOrder || sessionTypes === undefined || hydratedOrderSessionsRef.current) return
    if (editingOrder.sessions.length === 0) return
    if (serviceType !== editingOrder.serviceType) return

    const menuItemsBySession = new Map<string, OrderDetail['menuItems']>()
    for (const item of editingOrder.menuItems) {
      if (!item.orderEventId) continue
      menuItemsBySession.set(item.orderEventId, [
        ...(menuItemsBySession.get(item.orderEventId) ?? []),
        item,
      ])
    }

    let cancelled = false
    const extraItemsBySession = new Map<string, OrderDetail['menuItems']>()
    void Promise.all(
      editingOrder.sessions.map(async (s): Promise<SessionDraft> => {
        const mealPeriod = s.mealPeriod ?? 'DINNER'
        const meal = sittings.find((one) => one.key === mealPeriod)
        const start = parseISO(s.eventStart)
        const end = parseISO(s.eventEnd)
        const selections: Record<string, string[]> = {}
        const portions: Record<string, number> = {}
        const liveCooking: Record<string, boolean> = {}
        const customItems: Record<string, Dish[]> = {}
        let customizePackage = false
        const sessionItems = menuItemsBySession.get(s.id) ?? []
        // Told apart before anything else: an extra was deliberately never
        // part of the package, so checking it against the package's own
        // rules below would only ever fail to place it and mark the whole
        // sitting unmappable over dishes the package was never asked about.
        const items = sessionItems.filter((item) => !item.extra)
        extraItemsBySession.set(s.id, sessionItems.filter((item) => item.extra))
        // Converted from a quotation, which is most orders, a sitting keeps
        // its dishes but not the link to the package rule each one was
        // chosen under - so no package here is the ordinary case, not an
        // edge one, and dishes sitting on the order with nothing to check
        // them against are shown for what they are rather than hidden.
        let menuUnmappable = items.length > 0 && !s.packageId

        if (s.packageId && items.length > 0) {
          try {
            const rules = await api.get<PackageItem[]>(
              `/api/v1/packages/${s.packageId}/selection-rules`,
            )
            const ruleFor = new Map<string, PackageItem>()
            for (const rule of rules) {
              for (const choice of rule.choices) {
                ruleFor.set(choice.menuItemId, rule)
              }
            }
            for (const item of items) {
              const rule = ruleFor.get(item.menuItemId)
              if (rule) {
                const key = `${rule.id}:${item.menuItemId}`
                selections[rule.id] = [...(selections[rule.id] ?? []), item.menuItemId]
                const choice = rule.choices.find((c) => c.menuItemId === item.menuItemId)
                portions[key] = choice?.portion ?? 1
                if (item.liveCooking) liveCooking[key] = true
                continue
              }
              if (item.packageItemId) {
                // Not one of the package's own choices, but still tagged
                // against an item - the real one it was put under when
                // customized, or a whole new item added beyond the package's
                // own. Either way, shown back where it was put, not hidden.
                customItems[item.packageItemId] = [
                  ...(customItems[item.packageItemId] ?? []),
                  { id: item.menuItemId, itemCode: '', name: item.itemName, categoryId: item.categoryId },
                ]
                customizePackage = true
                continue
              }
              // Not the package's own dish by any rule this lookup can see,
              // and not tagged against an item either - resending a menu for
              // this sitting would read as the whole menu and drop it, so
              // nothing here is sent at all.
              menuUnmappable = true
            }
          } catch {
            // The rules could not be read just now - safer to leave the menu
            // alone than to send a guess.
            menuUnmappable = true
          }
        }

        return {
          key: `${mealPeriod}-${format(start, 'yyyy-MM-dd')}`,
          mealPeriod,
          mealLabel: meal?.label ?? humanise(mealPeriod),
          mealOrder: meal ? sittings.indexOf(meal) : s.sequenceNo,
          chargedByPax: meal?.chargedByPax ?? true,
          sessionName: s.eventName ?? '',
          sessionDate: format(start, 'yyyy-MM-dd'),
          startTime: format(start, 'HH:mm'),
          endTime: format(end, 'HH:mm'),
          hallId: s.hallId ?? '',
          hallSlotIds: s.hallSlotIds ?? [],
          serviceStyle: s.serviceStyle || 'LEAF',
          hallRentalAmount: '',
          pax: s.guestCount ? String(s.guestCount) : '',
          packageId: s.packageId ?? '',
          selections,
          menuRemarks: {},
          liveCooking,
          portions,
          id: s.id,
          knownRate: s.pricePerPerson != null ? String(s.pricePerPerson) : undefined,
          menuUnmappable,
          existingDishes: menuUnmappable ? items.map((item) => item.itemName) : undefined,
          existingMenuItemIds: items.length > 0
            ? items.map((item) => ({ menuItemId: item.menuItemId, liveCooking: item.liveCooking }))
            : undefined,
          customizePackage,
          customItems,
          customRate: customizePackage && s.pricePerPerson ? String(s.pricePerPerson) : undefined,
        }
      }),
    ).then(async (draftSessions) => {
      // Set only once the data actually lands: under StrictMode's dev-only
      // double-invoke, the throwaway first pass would otherwise mark this
      // hydrated and starve the real pass of ever running, while its own
      // (cancelled) result is silently dropped here anyway.
      if (cancelled) return
      hydratedOrderSessionsRef.current = true
      setSessions(draftSessions)

      // Extras are kept against the sitting they belong to, the same way a
      // package's own dishes are - read back here into the rows the counter
      // adds them from, keyed to the draft's own key rather than the
      // sitting's id, which the rest of this screen never sees.
      const rawExtras: { sessionKey: string; item: OrderDetail['menuItems'][number] }[] = []
      for (const draft of draftSessions) {
        for (const item of extraItemsBySession.get(draft.id ?? '') ?? []) {
          rawExtras.push({ sessionKey: draft.key, item })
        }
      }

      // The rate is kept on the order's own row since it can be agreed apart
      // from the catalogue, and since a recent enough row the row remembers
      // for itself whether that rate already carries its own GST - struck
      // once, and read back the same way from then on, rather than asked of
      // the catalogue again every time the order is opened. Only a row from
      // before that was tracked, or one missing its own tax rate, still
      // reads the catalogue - and only for what it is actually missing,
      // since the catalogue's own answer can have moved on since the row
      // was struck and is no longer the row's to trust for the rest.
      const needsRate = new Set(
        rawExtras.filter((one) => one.item.taxRatePercent == null).map((one) => one.item.menuItemId),
      )
      const needsInclusive = new Set(
        rawExtras.filter((one) => one.item.priceInclusiveTax == null).map((one) => one.item.menuItemId),
      )
      const allItemIds = [...new Set([...needsRate, ...needsInclusive])]
      const taxRateByItem = new Map<string, number>()
      const inclusiveByItem = new Map<string, boolean>()
      await Promise.all(
        allItemIds.map(async (menuItemId) => {
          try {
            const detail = await api.get<MenuItemType>(`/api/v1/menu/items/${menuItemId}`)
            if (needsRate.has(menuItemId)) {
              taxRateByItem.set(menuItemId, detail.taxRatePercent ?? 0)
            }
            if (needsInclusive.has(menuItemId)) {
              inclusiveByItem.set(menuItemId, Boolean(detail.priceInclusiveTax))
            }
          } catch {
            // Dropped from the catalogue since - taxed at nothing, and read as
            // excluding GST, rather than either being guessed at.
            if (needsRate.has(menuItemId)) {
              taxRateByItem.set(menuItemId, 0)
            }
            if (needsInclusive.has(menuItemId)) {
              inclusiveByItem.set(menuItemId, false)
            }
          }
        }),
      )
      if (cancelled) return

      const draftExtras: ExtraItem[] = rawExtras.map(({ sessionKey, item }) => ({
        key: item.id,
        kind: 'ITEM',
        menuItemId: item.menuItemId,
        item: null,
        description: item.itemName,
        priceInclusiveTax:
          item.priceInclusiveTax ?? inclusiveByItem.get(item.menuItemId) ?? false,
        quantity: String(item.quantity),
        unit: item.unit,
        unitPrice: String(item.additionalPrice ?? 0),
        taxRatePercent: String(item.taxRatePercent ?? taxRateByItem.get(item.menuItemId) ?? 0),
        sessionKey,
      }))
      setExtraItems(draftExtras)
    })
    return () => {
      cancelled = true
    }
  }, [editingOrder, sessionTypes, sittings, serviceType])

  const updateSession = (key: string, patch: Partial<SessionDraft>) =>
    setSessions((current) => current.map((s) => (s.key === key ? { ...s, ...patch } : s)))

  /** Ticking a meal adds that sitting on the chosen day. */
  const toggleMeal = (meal: Sitting, date: string, on: boolean) => {
    const key = `${meal.key}-${date}`
    const order = sittings.findIndex((s) => s.key === meal.key)
    setSessions((current) =>
      on
        ? [...current, newSession(meal, order, date, onPremises ? hallId : '',
            onPremises ? hallSlotIds : [], slotStartTime, onPremises ? slotRental : '')]
        : current.filter((s) => s.key !== key),
    )
  }

  // A quotation reads down the day in the order the office keeps its sittings,
  // which is the master's order and not this screen's opinion of one.
  const orderedSessions = useMemo(
    () =>
      [...sessions].sort((a, b) => {
        const byDate = a.sessionDate.localeCompare(b.sessionDate)
        if (byDate !== 0) return byDate
        return a.mealOrder - b.mealOrder
      }),
    [sessions],
  )

  // A service with no sittings assigned cannot be quoted by sitting, and an
  // empty step with no explanation reads as a broken screen rather than as
  // something to go and set up.
  const noSittings = Boolean(serviceType) && sessionTypes !== undefined && sittings.length === 0

  // Changing what is being sold changes the sittings on offer. A draft left
  // under one the new service is not assigned would be quoted under a meal that
  // service never sells.
  useEffect(() => {
    if (sessionTypes === undefined) return
    const offered = new Set(sittings.map((sitting) => sitting.key))
    setSessions((current) =>
      current.every((session) => offered.has(session.mealPeriod))
        ? current
        : current.filter((session) => offered.has(session.mealPeriod)),
    )
  }, [sessionTypes, sittings])

  /** The request body, shared by the preview and the save. */
  const requestBody = useMemo(() => {
    const packageById = new Map((packages ?? []).map((p) => [p.id, p]))
    return {
      enquiryId,
      customerId,
      outletId,
      serviceType,
      // Not sent at all where the service does not ask for one, rather than
      // sent as whatever happened to be at the top of the list.
      eventType: eventTypeRequired ? eventType : undefined,
      eventDate,
      guestCount: Math.max(...orderedSessions.map((s) => Number(s.pax || 0)), 0) || 1,
      childGuestCount: 0,
      hallId: onPremises ? hallId || undefined : undefined,
      deliveryAddress: deliveryAddress || undefined,
      address,
      venueDistanceKm: venueDistanceKm ? Number(venueDistanceKm) : undefined,
      venueMapUrl: venueMapUrl || undefined,
      fulfilmentMode: onPremises ? undefined : fulfilmentMode,
      manpowerRequired: onPremises ? true : manpowerRequired,
      validUntil,
      paymentTerms: paymentTerms || undefined,
      notes: notes || undefined,
      discountAmount: discountMode === 'AMOUNT' && discountValue ? Number(discountValue) : undefined,
      discountPercent: discountMode === 'PERCENT' && discountValue ? Number(discountValue) : undefined,
      discountRecommendedBy: discountRecommendedBy.trim() || undefined,
      discountReason: discountReason.trim() || undefined,
      sessions: orderedSessions.map((s, index) => {
        const pkg = packageById.get(s.packageId)
        return {
          sequenceNo: index + 1,
          sessionName: s.sessionName || undefined,
          mealPeriod: s.mealPeriod,
          sessionDate: s.sessionDate,
          startTime: `${s.startTime}:00`,
          hallId: s.hallId || undefined,
          hallSlotIds: s.hallSlotIds,
          serviceStyle: s.serviceStyle || undefined,
          hallSlotId: s.hallSlotIds[0],
          deliveryAddress: s.hallId ? undefined : deliveryAddress || undefined,
          address: s.hallId ? undefined : address,
          pax: Number(s.pax || 0),
          packageId: pkg?.id,
          packageVersionId: pkg?.currentVersionId,
          hallRentalAmount: s.hallRentalAmount ? Number(s.hallRentalAmount) : undefined,
          // A session being revised that isn't actually being customized is
          // still told apart from one priced fresh off the catalogue: its
          // own known rate is sent as if it were a custom one, so the
          // preview reads what the order will still say rather than what
          // the package costs today. Nothing here reaches the order itself
          // - the revise endpoint has its own, narrower rule for that.
          customizePackage: Boolean(s.customizePackage)
            || Boolean(orderEditId && !s.customizePackage && s.knownRate),
          customRatePerPerson:
            s.customizePackage && s.customRate
              ? Number(s.customRate)
              : orderEditId && !s.customizePackage && s.knownRate
                ? Number(s.knownRate)
                : undefined,
          // The known rate above is the order's own struck figure, already
          // taxed - not a rate being agreed now - so the preview is told to
          // read it that way rather than add tax to it a second time.
          knownRate: Boolean(orderEditId && !s.customizePackage && s.knownRate),
          // The extras charged against this sitting and no other.
          additionalLines: extraItems
            .filter((item) => item.sessionKey === s.key)
            .filter((item) => item.description.trim() !== '' && Number(item.quantity) > 0)
            .map((item) => ({
              // A package sold as a lot is a package on the bill, not a dish;
              // the printed documents read the type to know what it is.
              lineType: item.kind === 'PACKAGE' ? 'PACKAGE' : 'EXTRA_MENU_ITEM',
              referenceId:
                (item.kind === 'PACKAGE' ? item.packageId : item.menuItemId) || undefined,
              description: item.description.trim(),
              quantity: Number(item.quantity),
              unit: item.unit.trim() || 'NOS',
              unitPrice: Number(item.unitPrice || 0),
              // The rate came off the catalogue with the item. A projector is
              // an ordinary service at eighteen and the food is at five, and
              // nobody at a counter should be classifying that line by line.
              taxRatePercent:
                item.kind === 'PACKAGE' ? undefined : Number(item.taxRatePercent || 0) || undefined,
              priceInclusiveTax: item.kind === 'PACKAGE' ? undefined : item.priceInclusiveTax,
            })),
          menuSelections: pkg
            ? [
                ...Object.entries(s.selections).flatMap(([packageItemId, ids]) =>
                  ids.map((menuItemId) => ({
                    menuItemId,
                    packageItemId,
                    // The package says how much of a serving this choice is; a
                    // quarter portion means the kitchen makes a quarter per guest.
                    quantityPerGuest: s.portions[`${packageItemId}:${menuItemId}`] ?? 1,
                    liveCooking: s.liveCooking[`${packageItemId}:${menuItemId}`] ?? false,
                    notes: s.menuRemarks[`${packageItemId}:${menuItemId}`] || undefined,
                  })),
                ),
                // Dishes added apart from the package's own choices, only
                // while this sitting is being customized.
                ...(s.customizePackage
                  ? Object.entries(s.customItems ?? {}).flatMap(([packageItemId, dishes]) =>
                      dishes.map((dish) => ({
                        menuItemId: dish.id,
                        packageItemId,
                        quantityPerGuest: 1,
                        liveCooking: false,
                        notes: undefined,
                      })),
                    )
                  : []),
              ]
            : [],
          // Without a package the sitting is priced from its items, each with
          // its own quantity and rate. Nothing here is charged by the head.
          alaCarteItems: [],
        }
      }),
    }
  }, [
    enquiryId, customerId, outletId, serviceType, eventType, eventDate, hallId, onPremises,
    deliveryAddress, address, venueDistanceKm, venueMapUrl, fulfilmentMode, manpowerRequired,
    validUntil, paymentTerms, notes, discountMode, discountValue,
    discountRecommendedBy, discountReason, orderedSessions,
    packages, extraItems, orderEditId,
  ])

  /** Turns a date and an "HH:mm" into what the order's session endpoints take. */
  const toIso = (date: string, time: string) => new Date(`${date}T${time || '12:00'}`).toISOString()

  /** The request body for revising an order - a different shape from a
      quotation's, because the order's own session endpoints are. */
  const orderReviseBody = useMemo(() => {
    const packageById = new Map((packages ?? []).map((p) => [p.id, p]))
    return {
      eventType: eventTypeRequired ? eventType || undefined : undefined,
      fulfilmentMode: onPremises ? undefined : fulfilmentMode,
      manpowerRequired: onPremises ? true : manpowerRequired,
      notes: notes || undefined,
      // Sent as an explicit figure, never left out: a blank box is how a
      // discount already on the order is taken off it again, and leaving the
      // field out would be read as "nothing about this changed" instead.
      discountPercent: discountMode === 'PERCENT' ? Number(discountValue || 0) : 0,
      discountAmount: discountMode === 'AMOUNT' ? Number(discountValue || 0) : 0,
      discountRecommendedBy: discountRecommendedBy.trim() || undefined,
      discountReason: discountReason.trim() || undefined,
      sessions: orderedSessions.map((s) => {
        const pkg = packageById.get(s.packageId)
        const start = toIso(s.sessionDate, s.startTime)
        const end = toIso(s.sessionDate, s.endTime || s.startTime)
        return {
          id: s.id,
          eventName: s.sessionName || undefined,
          mealPeriod: s.mealPeriod,
          sessionDate: s.sessionDate,
          eventStart: start,
          // A sitting given no end of its own runs four hours - the same
          // fallback the order's own "add a session" endpoint uses when
          // asked for nothing better.
          eventEnd: end > start ? end : toIso(s.sessionDate, '23:59'),
          hallId: s.hallId || undefined,
          hallSlotId: s.hallSlotIds[0],
          hallSlotIds: s.hallSlotIds,
          guestCount: Number(s.pax || 0),
          serviceStyle: s.serviceStyle || undefined,
          deliveryAddress: deliveryAddress || undefined,
          address,
          venueDistanceKm: venueDistanceKm ? Number(venueDistanceKm) : undefined,
          venueMapUrl: venueMapUrl || undefined,
          packageVersionId: pkg?.currentVersionId,
          // What a head costs when this sitting's package has been agreed
          // apart from what it would otherwise charge; blank keeps the
          // package's own rate.
          pricePerPerson:
            s.customizePackage && s.customRate ? Number(s.customRate) : undefined,
          customizePackage: Boolean(s.customizePackage),
          menuChoices:
            !pkg || s.menuUnmappable
              ? undefined
              : [
                  ...Object.entries(s.selections).flatMap(([packageItemId, ids]) =>
                    ids.map((menuItemId) => ({
                      menuItemId,
                      packageItemId,
                      liveCooking: s.liveCooking[`${packageItemId}:${menuItemId}`] ?? false,
                    })),
                  ),
                  // Dishes added apart from the package's own choices, only
                  // while this sitting is being customized.
                  ...(s.customizePackage
                    ? Object.entries(s.customItems ?? {}).flatMap(([packageItemId, dishes]) =>
                        dishes.map((dish) => ({
                          menuItemId: dish.id,
                          packageItemId,
                          liveCooking: false,
                        })),
                      )
                    : []),
                ],
          // What was put on this sitting on top of the package - a package
          // sold as its own lot has no equivalent here, so only an ordinary
          // dish off the catalogue is sent. A charge typed in by hand - one
          // off the catalogue, like transport - carries no menuItemId and is
          // sent all the same; the preview above has always shown it, and it
          // used to vanish silently on save because this list alone demanded
          // one.
          extraItems: extraItems
            .filter((item) => item.sessionKey === s.key)
            .filter((item) => item.kind !== 'PACKAGE')
            .filter((item) => item.description.trim() !== '' && Number(item.quantity) > 0)
            .map((item) => ({
              menuItemId: item.menuItemId || undefined,
              itemName: item.description.trim() || item.item?.name || '',
              categoryId: item.item?.categoryId,
              quantity: Number(item.quantity),
              unit: item.unit.trim() || 'NOS',
              additionalPrice: Number(item.unitPrice || 0),
              taxRatePercent: item.taxRatePercent ? Number(item.taxRatePercent) : undefined,
              // What this screen struck the rate at, so the row remembers
              // it for itself rather than it being re-guessed from the
              // catalogue - which can read differently by the time anybody
              // next opens this order.
              priceInclusiveTax: item.priceInclusiveTax ?? false,
            })),
        }
      }),
    }
  }, [
    eventTypeRequired, eventType, onPremises, fulfilmentMode, manpowerRequired, notes,
    discountMode, discountValue, discountRecommendedBy, discountReason, orderedSessions,
    deliveryAddress, address, venueDistanceKm, venueMapUrl, packages, extraItems,
  ])

  // Re-price on the server whenever anything that moves money changes. An
  // order being revised prices the same way - each session sends its own
  // known rate as the preview's "agreed apart from the package" figure, so
  // this reads what the order will still say rather than today's catalogue.
  useEffect(() => {
    if (!customerId || !outletId || orderedSessions.length === 0) {
      setQuote(null)
      return
    }
    const timer = setTimeout(() => {
      previewQuote.mutate(requestBody, { onSuccess: setQuote })
    }, 400)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestBody])

  // A service is as required as the customer: without one there is nothing to
  // price, and an outlet that sells none cannot be quoted from at all.
  const eventStepValid = Boolean(
    customerId && outletId && eventDate && serviceType && (!eventTypeRequired || eventType),
  )
    && (!onPremises || Boolean(hallId))
    && !addressMissing
  // A sitting sold by the quantity has nothing to give here: what it comes to
  // is settled on the next step, line by line.
  const sessionStepValid =
    orderedSessions.length > 0
    && orderedSessions.every((s) => !s.chargedByPax || Number(s.pax) > 0)
    // A head count the branch will not take is not a step that can be
    // continued from: the server refuses it, and it is better refused here.
    && orderedSessions.every((s) => !outOfRange(s.pax, paxFloor, paxCeiling))
  // An order has no preview to be valid or not; what stands in for it is
  // every packaged sitting actually carrying a choice, the same floor the
  // preview's own validation holds a quotation to.
  const menuStepValid = orderEditId
    ? orderedSessions.every((s) => !s.packageId || s.menuUnmappable || Object.keys(s.selections).length > 0)
    : (quote?.valid ?? false)

  const submit = () =>
    editingOrder
      ? reviseOrder.mutate(
          { id: editingOrder.id, body: orderReviseBody },
          { onSuccess: (updated) => navigate(`/orders/${updated.id}`) },
        )
      : editingQuotation
        ? revise.mutate(
            { id: editingQuotation.id, body: requestBody },
            { onSuccess: (updated) => navigate(`/quotations/${updated.id}`) },
          )
        : asOrder
          ? createOrder.mutate(
              { quotation: requestBody, orderedByName: orderedByName.trim() || undefined,
                confirmImmediately: false,
                orderNumber: isAdmin && orderNumber.trim() ? orderNumber.trim() : undefined },
              { onSuccess: (created) => navigate(`/orders/${created.id}`) },
            )
          : create.mutate(requestBody, { onSuccess: (created) => navigate(`/quotations/${created.id}`) })

  // Nothing to show yet: waiting on the quotation being revised, or - once it
  // has arrived - on the header hydration and the sittings master its
  // sessions are built against, so the form never flashes the wrong ones.
  if (
    editId &&
    (!editingQuotation ||
      (editingQuotation.currentVersion &&
        (serviceType !== editingQuotation.currentVersion.serviceType || sessionTypes === undefined)))
  ) {
    return <InlineSpinner />
  }
  // The same wait, on the order side.
  if (
    orderEditId &&
    (!editingOrder || serviceType !== editingOrder.serviceType || sessionTypes === undefined)
  ) {
    return <InlineSpinner />
  }

  return (
    <>
      <PageHeader
        title={
          editingOrder
            ? `Revise ${editingOrder.orderNumber}`
            : editingQuotation
              ? `Revise ${editingQuotation.quotationNumber}`
              : asOrder
                ? 'New order'
                : 'New quotation'
        }
        subtitle={
          editingOrder
            ? 'Saved straight to the order - past the change-request desk, not through it.'
            : editingQuotation
              ? 'Saving raises a new version and supersedes this one; nothing already accepted is changed.'
              : enquiry
                ? `From enquiry ${enquiry.enquiryNumber} · ${enquiry.customerName}`
                : asOrder
                  ? 'Taken without a quotation. One or more sessions, each with its own pax and menu.'
                  : 'One or more sessions, each with its own pax and menu'
        }
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              navigate(
                editingOrder
                  ? `/orders/${editingOrder.id}`
                  : editingQuotation
                    ? `/quotations/${editingQuotation.id}`
                    : asOrder
                      ? '/orders'
                      : '/quotations',
              )
            }
          >
            Back
          </Button>
        }
      />

      <ErrorBanner error={create.error ?? createOrder.error ?? revise.error ?? reviseOrder.error} />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Stepper activeStep={step} sx={{ mb: 3 }}>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {step === 0 && asOrder ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Confirm the order now with what you have, and settle the sessions, the menu and
                  the charges on the order afterwards. Or carry on through the steps if you have it
                  all.
                </Alert>
              ) : null}

              {step === 0 && (
                <EventStep
                  customer={customer}
                  setCustomer={setCustomer}
                  outlets={outlets ?? []}
                  outletId={outletId}
                  setOutletId={(id) => {
                    setOutletId(id)
                    setHallId('')
                  }}
                  serviceType={serviceType}
                  eventTypeRequired={eventTypeRequired}
                  occasions={occasions ?? []}
                  setServiceType={setServiceType}
                  eventType={eventType}
                  setEventType={setEventType}
                  eventDate={eventDate}
                  setEventDate={setEventDate}
                  onPremises={onPremises}
                  needsAddress={needsAddress}
                  addressMissing={addressMissing}
                  services={bookable}
                  spaceLabel={spaceType === 'DINING' ? 'Dining room' : 'Banquet hall'}
                  halls={halls ?? []}
                  hallId={hallId}
                  hallSlotIds={hallSlotIds}
                  onSlotsChosen={(slotIds, startTime, rental) => {
                    setHallSlotIds(slotIds)
                    setSlotStartTime(startTime)
                    setSlotRental(rental)
                    // The sittings already added take the change too, so the
                    // slot chosen here is the slot they are all booked in.
                    setSessions((current) =>
                      current.map((session) => ({
                        ...session,
                        hallSlotIds: slotIds,
                        startTime: startTime || session.startTime,
                        hallRentalAmount: rental,
                      })),
                    )
                  }}
                  excludeOrderId={orderEditId}
                  setHallId={setHallId}
                  deliveryAddress={deliveryAddress}
                  address={address}
                  setAddress={setAddress}
                  venueDistanceKm={venueDistanceKm}
                  setVenueDistanceKm={setVenueDistanceKm}
                  venueMapUrl={venueMapUrl}
                  setVenueMapUrl={setVenueMapUrl}
                  fulfilmentMode={fulfilmentMode}
                  setFulfilmentMode={setFulfilmentMode}
                  manpowerRequired={manpowerRequired}
                  setManpowerRequired={setManpowerRequired}
                  setDeliveryAddress={setDeliveryAddress}
                />
              )}

              {step === 1 && (
                <SessionStep
                  eventDate={eventDate}
                  sessions={sessions}
                  orderedSessions={orderedSessions}
                  sittings={sittings}
                  noSittings={noSittings}
                  toggleMeal={toggleMeal}
                  updateSession={updateSession}
                  removeSession={(key) => setSessions((c) => c.filter((s) => s.key !== key))}
                  onPremises={onPremises}
                  halls={halls ?? []}
                  paxFloor={paxFloor}
                  paxCeiling={paxCeiling}
                />
              )}

              {step === 2 && (
                <MenuStep
                  sessions={orderedSessions}
                  extraItems={extraItems}
                  setExtraItems={setExtraItems}
                  packages={packages ?? []}
                  updateSession={updateSession}
                  quote={quote}
                />
              )}

              {step === 3 && (
                <ChargesStep
                  discountMode={discountMode}
                  setDiscountMode={setDiscountMode}
                  discountValue={discountValue}
                  setDiscountValue={setDiscountValue}
                  discountRecommendedBy={discountRecommendedBy}
                  setDiscountRecommendedBy={setDiscountRecommendedBy}
                  discountReason={discountReason}
                  setDiscountReason={setDiscountReason}
                  validUntil={validUntil}
                  setValidUntil={setValidUntil}
                  paymentTerms={paymentTerms}
                  setPaymentTerms={setPaymentTerms}
                  notes={notes}
                  setNotes={setNotes}
                  asOrder={asOrder}
                  orderedByName={orderedByName}
                  setOrderedByName={setOrderedByName}
                  isAdmin={isAdmin}
                  orderNumber={orderNumber}
                  setOrderNumber={setOrderNumber}
                  // An order has neither a validity date nor payment terms of
                  // its own - both belong to the quotation it may have come
                  // from, not to the order itself.
                  hideQuotationFields={Boolean(orderEditId)}
                />
              )}

              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Button disabled={step === 0} onClick={() => setStep((c) => c - 1)}>
                  Back
                </Button>
                {step < 3 ? (
                  <Stack direction="row" spacing={1}>
                    {/* An order taken at the counter is often a customer, an
                        occasion and a date, and nothing more yet. The sessions,
                        the menu and the charges are settled on the order itself
                        afterwards, so the rest of this is offered rather than
                        insisted on. A quotation is a document sent to somebody
                        and still needs all of it. */}
                    {asOrder ? (
                      <Button
                        variant="outlined"
                        onClick={submit}
                        // Taking it now skips the rest of the steps; it does
                        // not skip what the branch will take.
                        disabled={
                          !eventStepValid
                          || createOrder.isPending
                          || orderedSessions.some((s) => outOfRange(s.pax, paxFloor, paxCeiling))
                        }
                      >
                        Confirm Order Now
                      </Button>
                    ) : null}
                    <Button
                      variant="contained"
                      onClick={() => setStep((c) => c + 1)}
                      disabled={
                        (step === 0 && !eventStepValid)
                        || (step === 1 && !sessionStepValid)
                        || (step === 2 && !menuStepValid)
                      }
                    >
                      Continue
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    variant="contained"
                    onClick={submit}
                    disabled={
                      !menuStepValid
                      || addressMissing
                      || create.isPending
                      || createOrder.isPending
                      || revise.isPending
                      || reviseOrder.isPending
                    }
                  >
                    {/* The same words as the shortcut on the earlier steps: it is
                        the same act, and two names for it read as two things. */}
                    {editingOrder
                      ? 'Save to the order'
                      : editingQuotation
                        ? 'Save revision'
                        : asOrder
                          ? 'Confirm Order Now'
                          : 'Create quotation'}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <PricePanel
            quote={quote}
            pending={previewQuote.isPending}
            error={previewQuote.error}
            revisingOrder={Boolean(orderEditId)}
            sessions={orderedSessions}
            halls={halls}
          />
        </Grid>
      </Grid>
    </>
  )
}

// ---------------------------------------------------------------- step one

function EventStep(props: {
  customer: CustomerSummary | null
  setCustomer: (customer: CustomerSummary | null) => void
  outlets: { id: string; name: string }[]
  outletId: string
  setOutletId: (id: string) => void
  serviceType: string
  setServiceType: (value: string) => void
  eventType: string
  setEventType: (value: string) => void
  /** Whether this line of business asks what occasion it is for. */
  eventTypeRequired: boolean
  /** The occasions it is sold for, as the office keeps them. */
  occasions: { code: string; name: string }[]
  eventDate: string
  setEventDate: (value: string) => void
  onPremises: boolean
  /** An address is asked for only where the food leaves the premises. */
  needsAddress: boolean
  /** Whether enough of an address has been given to find the place. */
  addressMissing: boolean
  services: { code: string; name: string }[]
  /** "Banquet hall" or "Dining room", whichever is being booked. */
  spaceLabel: string
  halls: { id: string; name: string; seatingCapacity: number; rentalCharge: number }[]
  hallId: string
  setHallId: (id: string) => void
  hallSlotIds: string[]
  onSlotsChosen: (slotIds: string[], startTime: string, rental: string) => void
  /** The order already being revised, so the slot board does not call its own slot taken. */
  excludeOrderId?: string
  deliveryAddress: string
  address: DeliveryAddress
  setAddress: (next: DeliveryAddress) => void
  venueDistanceKm: string
  setVenueDistanceKm: (value: string) => void
  venueMapUrl: string
  setVenueMapUrl: (value: string) => void
  fulfilmentMode: string
  setFulfilmentMode: (value: string) => void
  manpowerRequired: boolean
  setManpowerRequired: (value: boolean) => void
  setDeliveryAddress: (value: string) => void
}) {
  const [addingCustomer, setAddingCustomer] = useState(false)

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flexGrow: 1 }}>
            <CustomerPicker
              value={props.customer}
              onChange={(chosen) => props.setCustomer(chosen)}
              required
            />
          </Box>
          {/* An order taken at the counter is often the first time we have met
              them. Adding them here keeps the rest of the form. */}
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            sx={{ mt: 1, whiteSpace: 'nowrap' }}
            onClick={() => setAddingCustomer(true)}
          >
            Add customer
          </Button>
        </Stack>
        <NewCustomerDialog
          open={addingCustomer}
          onCreated={(customer) => {
            // Somebody entered a moment ago is in no list the picker has asked
            // for yet, so they go straight into the box.
            props.setCustomer(customer)
          }}
          onClose={() => setAddingCustomer(false)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          select
          fullWidth
          required
          label="Outlet"
          value={props.outletId}
          onChange={(event) => props.setOutletId(event.target.value)}
        >
          {props.outlets.map((outlet) => (
            <MenuItem key={outlet.id} value={outlet.id}>
              {outlet.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          select
          fullWidth
          label="Service"
          value={props.serviceType}
          onChange={(event) => props.setServiceType(event.target.value)}
        >
          {props.services.map((type) => (
            <MenuItem key={type.code} value={type.code}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {/* A wedding has an occasion; a counter sale has none, and asking for
          one made somebody pick "Wedding" off a list to get past the screen.
          Which lines of business ask is set against the service. */}
      {props.eventTypeRequired ? (
        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            required
            label="Event type"
            value={props.eventType}
            onChange={(event) => props.setEventType(event.target.value)}
          >
            {props.occasions.map((occasion) => (
              <MenuItem key={occasion.code} value={occasion.code}>
                {occasion.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      ) : null}
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          type="date"
          label="Event date"
          value={props.eventDate}
          onChange={(event) => props.setEventDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      {/* Banquet means a hall, so the list appears as soon as it is chosen. */}
      {props.onPremises ? (
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            {props.spaceLabel}
          </Typography>
          {props.halls.length === 0 ? (
            <Alert severity="info">
              This outlet has no {props.spaceLabel.toLowerCase()}s. Pick another outlet or service type.
            </Alert>
          ) : (
            <Grid container spacing={1}>
              {props.halls.map((candidate) => {
                const selected = candidate.id === props.hallId
                return (
                  <Grid item xs={12} sm={6} key={candidate.id}>
                    <Box
                      onClick={() => props.setHallId(candidate.id)}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: (theme) =>
                          `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                        bgcolor: selected ? 'action.selected' : 'background.paper',
                      }}
                    >
                      <Typography variant="body2">{candidate.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        seats {candidate.seatingCapacity} · rental {formatMoney(candidate.rentalCharge)}
                      </Typography>
                    </Box>
                  </Grid>
                )
              })}
            </Grid>
          )}
          {/* The slots belong with the hall: which part of the day is being
              booked is the same decision as which room. Every session takes
              them, so it is asked once here rather than on each meal. */}
          {props.hallId ? (
            <Box sx={{ mt: 1.5 }}>
              <EventSlotPicker
                hallId={props.hallId}
                eventDate={props.eventDate}
                chosen={props.hallSlotIds}
                onChange={props.onSlotsChosen}
                excludeOrderId={props.excludeOrderId}
              />
            </Box>
          ) : null}
        </Grid>
      ) : props.needsAddress ? (
        <>
          <DeliveryAddressFields
            value={props.address}
            onChange={(patch) => props.setAddress({ ...props.address, ...patch })}
          />
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Anything else about finding the place"
              value={props.deliveryAddress}
              onChange={(event) => props.setDeliveryAddress(event.target.value)}
              helperText="Optional. The address above is what prints on the documents."
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Distance"
              value={props.venueDistanceKm}
              onChange={(event) => props.setVenueDistanceKm(event.target.value)}
              helperText="Kilometres, one way"
              inputProps={{ min: 0, step: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <TextField
              fullWidth
              label="Google Maps location"
              value={props.venueMapUrl}
              onChange={(event) => props.setVenueMapUrl(event.target.value)}
              placeholder="Paste the map link, or leave it blank to search the address"
              helperText={
                props.venueMapUrl
                  ? 'The driver is sent this pin'
                  : 'Without a pin the driver gets only the written address'
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Delivery or pickup"
              value={props.fulfilmentMode}
              onChange={(event) => props.setFulfilmentMode(event.target.value)}
              helperText={
                props.fulfilmentMode === 'PICKUP'
                  ? 'The customer collects; no vehicle is sent'
                  : 'We take the food to the venue'
              }
            >
              <MenuItem value="DELIVERY">Delivery</MenuItem>
              <MenuItem value="PICKUP">Pickup</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={8}>
            <FormControlLabel
              control={
                <Switch
                  checked={props.manpowerRequired}
                  onChange={(event) => props.setManpowerRequired(event.target.checked)}
                />
              }
              label="We send staff"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {props.manpowerRequired
                ? 'A roster is worked out for this job when the order is confirmed.'
                : 'Food only — the customer serves it. Nothing is rostered and no shortage is raised.'}
            </Typography>
          </Grid>
          {props.addressMissing ? (
            <Grid item xs={12}>
              <Alert severity="warning">
                Give at least a street or an area. A landmark on its own is how a driver ends up
                ringing the office from a junction.
              </Alert>
            </Grid>
          ) : null}
        </>
      ) : null}
    </Grid>
  )
}

// ---------------------------------------------------------------- step two

/** Whether a head count sits outside what this branch takes for the job. */
function outOfRange(pax: string, floor: number | null, ceiling: number | null) {
  const value = Number(pax || 0)
  if (value <= 0) return false
  return (floor != null && value < floor) || (ceiling != null && value > ceiling)
}

/** What the box says under itself: the range, or which way the number is wrong. */
function paxHelp(pax: string, floor: number | null, ceiling: number | null) {
  const value = Number(pax || 0)
  if (outOfRange(pax, floor, ceiling)) {
    return floor != null && value < floor
      ? `This is taken for ${floor} or more`
      : `This is taken for up to ${ceiling}`
  }
  if (floor != null && ceiling != null) return `Between ${floor} and ${ceiling}`
  if (floor != null) return `${floor} or more`
  if (ceiling != null) return `Up to ${ceiling}`
  return 'How many are expected'
}

function SessionStep(props: {
  eventDate: string
  sessions: SessionDraft[]
  orderedSessions: SessionDraft[]
  sittings: Sitting[]
  noSittings: boolean
  toggleMeal: (meal: Sitting, date: string, on: boolean) => void
  updateSession: (key: string, patch: Partial<SessionDraft>) => void
  removeSession: (key: string) => void
  onPremises: boolean
  halls: { id: string; name: string; rentalCharge: number }[]
  /** How many this job may be taken for here; null either side is no limit. */
  paxFloor: number | null
  paxCeiling: number | null
}) {
  const has = (mealKey: string, date: string) =>
    props.sessions.some((s) => s.key === `${mealKey}-${date}`)

  const dayPicker = (date: string, title: string) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="caption" color="text.secondary">
        {formatDate(date)}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        {props.sittings.map((meal) => (
          <FormControlLabel
            key={`${meal.key}-${date}`}
            control={
              <Checkbox
                size="small"
                checked={has(meal.key, date)}
                onChange={(event) => props.toggleMeal(meal, date, event.target.checked)}
              />
            }
            label={meal.label}
          />
        ))}
      </Stack>
    </Box>
  )

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tick every session being booked, then give each one its expected pax.
      </Typography>

      {props.noSittings ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This line of business has no sessions assigned, so there is nothing to book against.
          Assign them under Settings → Sessions.
        </Alert>
      ) : (
        dayPicker(props.eventDate, 'Sessions on the day')
      )}

      {props.orderedSessions.length === 0 ? (
        props.noSittings ? null : <Alert severity="info">Choose at least one session.</Alert>
      ) : (
        <Stack spacing={2}>
          {props.orderedSessions.map((session) => {
            return (
              <Card key={session.key} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    {/* The sitting, not the date. Every sitting of a function
                        is on the day given at the top, and repeating it on each
                        card was the same date written four times.

                        A booking that runs over more than one day is the
                        exception, and there the date is the only thing telling
                        two lunches apart. */}
                    <Typography variant="subtitle2">
                      {session.mealLabel}
                      {props.orderedSessions.some(
                        (other) => other.sessionDate !== session.sessionDate,
                      )
                        ? ` · ${formatDate(session.sessionDate)}`
                        : ''}
                    </Typography>
                    <IconButton size="small" onClick={() => props.removeSession(session.key)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  <Grid container spacing={2}>
                    {/* A sitting sold by the quantity has no head count and
                        nothing plated: both would be numbers somebody had to
                        invent, and neither would move the price. */}
                    {session.chargedByPax ? (
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          required
                          type="number"
                          label="Pax"
                          value={session.pax}
                          onChange={(event) =>
                            props.updateSession(session.key, { pax: event.target.value })
                          }
                          // Said while it is being typed. It was said by the
                          // server after Continue, three fields and a click
                          // later than it was useful.
                          error={outOfRange(session.pax, props.paxFloor, props.paxCeiling)}
                          helperText={paxHelp(session.pax, props.paxFloor, props.paxCeiling)}
                          inputProps={{
                            min: props.paxFloor ?? 1,
                            ...(props.paxCeiling != null ? { max: props.paxCeiling } : {}),
                          }}
                        />
                      </Grid>
                    ) : null}
                    <Grid item xs={6} md={3}>
                      <TimeField
                        label="Time"
                        value={session.startTime}
                        onChange={(value) => props.updateSession(session.key, { startTime: value })}
                        helperText="When it starts"
                      />
                    </Grid>
                    {session.chargedByPax ? (
                      <Grid item xs={6} md={3}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Service"
                          value={session.serviceStyle}
                          onChange={(event) =>
                            props.updateSession(session.key, { serviceStyle: event.target.value })
                          }
                          helperText="How it is served"
                        >
                          <MenuItem value="BUFFET">Buffet</MenuItem>
                          <MenuItem value="LEAF">Leaf service</MenuItem>
                        </TextField>
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={6}>
                        <Alert severity="info" sx={{ py: 0 }}>
                          Quoted as packages and items with a quantity and a rate, on the next
                          step. No pax, and nothing is plated.
                        </Alert>
                      </Grid>
                    )}
                    {/* The hall, its slots and the rental were settled on the
                        first step; a sitting is when it is served, for how many
                        and how, and nothing else. */}
                  </Grid>
                </CardContent>
              </Card>
            )
          })}
        </Stack>
      )}
    </>
  )
}

// -------------------------------------------------------------- step three

function MenuStep(props: {
  sessions: SessionDraft[]
  packages: CateringPackage[]
  updateSession: (key: string, patch: Partial<SessionDraft>) => void
  quote: QuotationPreview | null
  extraItems: ExtraItem[]
  setExtraItems: (items: ExtraItem[]) => void
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Each session gets its own package. Leave it blank and the session is priced from the items
        listed against it, each with its own quantity and rate.
      </Typography>

      {props.sessions.map((session) => (
        <SessionMenu
          key={session.key}
          session={session}
          packages={props.packages}
          updateSession={props.updateSession}
          extraItems={props.extraItems}
          setExtraItems={props.setExtraItems}
          issues={
            props.quote?.sessions.find(
              (s) => s.mealPeriod === session.mealPeriod && s.sessionDate === session.sessionDate,
            )?.menuIssues ?? []
          }
        />
      ))}
    </Stack>
  )
}

/**
 * The slot of the hall this sitting takes.
 *
 * <p>Halls are sold by the slot - the evening at the Emerald, the lunch at the
 * Grand - so the sitting names one and the hours and the rental follow from it.
 * The board says which are still open on that date, and a slot already held
 * shows who has it, so the office can call them rather than turn the customer
 * away or, worse, promise a hall twice.</p>
 */
/**
 * The slots the event holds, chosen once beside the hall.
 *
 * <p>Which part of the day is being booked is the same decision as which room,
 * so it is asked here rather than on every meal of the same day. Each slot is
 * rented in its own right, and the rental adds up as they are ticked.</p>
 */
function EventSlotPicker(props: {
  hallId: string
  eventDate: string
  chosen: string[]
  onChange: (slotIds: string[], startTime: string, rental: string) => void
  /** The order already being revised, so the board does not call its own slot taken. */
  excludeOrderId?: string
}) {
  const { data: board, isLoading, error } = useHallSlotBoard(props.hallId, props.eventDate,
    props.eventDate, props.excludeOrderId)
  const slots = board?.[0]?.slots ?? []

  if (!props.hallId) return null

  return (
    <>
      <Typography variant="caption" color="text.secondary">
        Slots — tick every one this booking takes. Each is rented in its own right.
      </Typography>
      {isLoading ? (
        <Typography variant="caption" display="block" color="text.secondary">
          Reading the hall board…
        </Typography>
      ) : error ? (
        // Told apart from a hall that genuinely has none. A board that could
        // not be read was reported as "no slots set up", which sent somebody
        // to the hall master to add slots it already had.
        <Typography variant="caption" display="block" color="error">
          The hall board could not be read just now. The session times are used as they are.
        </Typography>
      ) : slots.length === 0 ? (
        <Typography variant="caption" display="block" color="text.secondary">
          This hall has no slots set up. The session times are used as they are.
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          {slots.map((slot) => {
            const picked = props.chosen.includes(slot.slotId)
            return (
              <Chip
                key={slot.slotId}
                size="small"
                variant={picked ? 'filled' : 'outlined'}
                color={picked ? 'primary' : slot.available ? 'default' : 'error'}
                // A taken slot is still clickable: a manager may book over it
                // with a reason, and refusing the click would hide that.
                title={
                  slot.available
                    ? `${formatSlotHours(slot.startTime, slot.endTime)} — ${formatMoney(slot.rentalCharge)}`
                    : `${formatSlotHours(slot.startTime, slot.endTime)} — held by ${slot.heldBy ?? 'another booking'}`
                }
                label={`${slot.name} ${formatSlotHours(slot.startTime, slot.endTime)}${
                  slot.available ? '' : ' · taken'
                }`}
                onClick={() => {
                  const held = picked
                    ? props.chosen.filter((id) => id !== slot.slotId)
                    : [...props.chosen, slot.slotId]
                  // The booking starts at the earliest slot it holds and is
                  // rented for all of them: two slots are two rentals.
                  const order = slots
                    .filter((candidate) => held.includes(candidate.slotId))
                    .sort((left, right) => left.startTime.localeCompare(right.startTime))
                  props.onChange(
                    held,
                    order.length > 0 ? order[0].startTime.slice(0, 5) : '',
                    order.length > 0
                      ? String(order.reduce((sum, candidate) => sum + candidate.rentalCharge, 0))
                      : '',
                  )
                }}
              />
            )
          })}
        </Stack>
      )}
      {slots.some((slot) => !slot.available && props.chosen.includes(slot.slotId)) ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          A slot ticked here is already held. Converting this to an order will need a manager's
          override with a reason.
        </Alert>
      ) : null}
    </>
  )
}

function SessionMenu(props: {
  session: SessionDraft
  packages: CateringPackage[]
  updateSession: (key: string, patch: Partial<SessionDraft>) => void
  issues: string[]
  extraItems: ExtraItem[]
  setExtraItems: (items: ExtraItem[]) => void
}) {
  const { session } = props
  const [rules, setRules] = useState<PackageItem[]>([])

  // Rules depend on the package chosen for this sitting alone.
  useEffect(() => {
    let cancelled = false
    // Left alone on purpose: a dish this screen could not place against any
    // of the package's rules means the rest of the sitting's menu cannot be
    // trusted to redraw correctly either, so nothing here is auto-filled -
    // that would show choices as current that are only a guess.
    if (!session.packageId || session.menuUnmappable) {
      setRules([])
      return
    }
    api
      .get<PackageItem[]>(`/api/v1/packages/${session.packageId}/selection-rules`)
      .then((loaded) => {
        if (cancelled) return
        setRules(loaded)
        // Preselect from what is already on the sitting where this package's
        // rules recognise it - a package picked to replace one this screen
        // could not check against should pick up the real menu, not start it
        // blank - filled out to the minimum with the mandatory items and then
        // whatever comes first. None of this runs where the rule already has
        // a value, whether that is a quotation loaded for revision or the
        // same package picked a second time, and it is kept to this
        // package's own rules and nothing else, so switching away and back
        // never leaves another package's choices mixed into this one.
        const existingIds = new Map(
          (session.existingMenuItemIds ?? []).map((item) => [item.menuItemId, item.liveCooking]),
        )
        const next: Record<string, string[]> = {}
        const portions: Record<string, number> = {}
        const liveCooking: Record<string, boolean> = { ...session.liveCooking }
        for (const rule of loaded) {
          const already = session.selections[rule.id]
          if (already !== undefined) {
            next[rule.id] = already
          } else {
            const mandatory = rule.choices.filter((c) => c.mandatory).map((c) => c.menuItemId)
            const onSitting = rule.choices
              .map((c) => c.menuItemId)
              .filter((id) => existingIds.has(id) && !mandatory.includes(id))
            const picked = [...mandatory, ...onSitting]
            const filler = rule.choices
              .filter((c) => !picked.includes(c.menuItemId))
              .slice(0, Math.max(0, rule.minSelections - picked.length))
              .map((c) => c.menuItemId)
            next[rule.id] = [...picked, ...filler]
            for (const id of picked) {
              if (existingIds.get(id)) liveCooking[`${rule.id}:${id}`] = true
            }
          }
          for (const choice of rule.choices) {
            const key = `${rule.id}:${choice.menuItemId}`
            portions[key] = session.portions[key] ?? choice.portion ?? 1
          }
        }
        props.updateSession(session.key, { selections: next, portions, liveCooking })
      })
      .catch(() => setRules([]))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.packageId])

  const toggleChoice = (ruleId: string, menuItemId: string, on: boolean) => {
    const current = session.selections[ruleId] ?? []
    const remarks = { ...session.menuRemarks }
    if (!on) {
      // A note cannot outlive the choice it was written against.
      delete remarks[`${ruleId}:${menuItemId}`]
    }
    props.updateSession(session.key, {
      selections: {
        ...session.selections,
        [ruleId]: on ? [...current, menuItemId] : current.filter((id) => id !== menuItemId),
      },
      menuRemarks: remarks,
    })
  }

  const setRemark = (ruleId: string, menuItemId: string, text: string) =>
    props.updateSession(session.key, {
      menuRemarks: { ...session.menuRemarks, [`${ruleId}:${menuItemId}`]: text.slice(0, 200) },
    })

  const setLiveCooking = (ruleId: string, menuItemId: string, on: boolean) =>
    props.updateSession(session.key, {
      liveCooking: { ...session.liveCooking, [`${ruleId}:${menuItemId}`]: on },
    })


  // A breakfast package has no business being offered for the reception dinner.
  // Packages sold for any session stay on every list.
  //
  // The hall is checked again here, and not only when the list was asked for:
  // one sitting of a booking can be moved to another hall, and the menu written
  // against the hall it left is not the menu for the hall it lands in.
  const forThisSitting = props.packages.filter(
    (candidate) =>
      (candidate.sessionType === 'ANY' || candidate.sessionType === session.mealPeriod)
      && (!candidate.hallIds?.length || !session.hallId || candidate.hallIds.includes(session.hallId)),
  )

  // An item added apart from the package's own: it has no rule behind it, so
  // it is known only by the key its dishes are kept under in customItems.
  // Numbered to carry on after the package's own items, in the order each
  // was added.
  const knownRuleIds = new Set(rules.map((rule) => rule.id))
  const extraItemIds = Object.keys(session.customItems ?? {}).filter(
    (id) => !knownRuleIds.has(id),
  )
  const highestItemNumber = Math.max(0, ...rules.map((rule) => rule.itemNumber))
  const addExtraItem = () =>
    // A real UUID, not a label: this travels to the server as packageItemId,
    // typed there as one, and a string that is not one fails to deserialize
    // at all rather than being refused with a word of explanation.
    props.updateSession(session.key, {
      customItems: { ...session.customItems, [newId()]: [] },
    })
  const removeExtraItem = (id: string) => {
    const next = { ...session.customItems }
    delete next[id]
    props.updateSession(session.key, { customItems: next })
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2">
            {session.sessionName || session.mealLabel} · {formatDate(session.sessionDate)}
          </Typography>
          {session.chargedByPax ? (
            <Chip size="small" variant="outlined" label={`${session.pax || 0} pax`} />
          ) : (
            <Chip size="small" variant="outlined" label="By the quantity" />
          )}
        </Stack>

        {/* A package here is priced per head times the pax. A sitting sold by
            the quantity has no pax to multiply by, so its packages go on below
            as lines with their own quantity and rate. */}
        {session.chargedByPax ? (
        <TextField
          select
          fullWidth
          size="small"
          label="Package"
          value={session.packageId}
          onChange={(event) =>
            // Picking a package here - even a first one, on a sitting whose
            // existing dishes could not be matched to anything - is a fresh
            // choice to build from the rules below, not a guess at what was
            // already cooking. The old "can't be trusted" state belongs to
            // the package that couldn't be read, not to whatever is chosen
            // next.
            //
            // The order's known rate belongs to the package it was struck
            // against, too. Left in place, it is sent back as the "custom"
            // rate for whatever is chosen next - a different package's own
            // price, silently overridden by the old one's, then taxed again
            // on top of a figure that already carried its own tax.
            props.updateSession(session.key, {
              packageId: event.target.value,
              menuUnmappable: false,
              existingDishes: undefined,
              knownRate: undefined,
              customRate: undefined,
            })
          }
          helperText="Blank prices this session from the items listed below"
          sx={{ mb: 2 }}
        >
          <MenuItem value="">No package — choose dishes</MenuItem>
          {forThisSitting.map((candidate) => (
            <MenuItem key={candidate.id} value={candidate.id}>
              {candidate.name} — {formatMoney(candidate.basePricePerPerson)} per head
            </MenuItem>
          ))}
        </TextField>
        ) : null}

        {/* A customer asking to swap, drop or add a dish apart from what the
            package itself offers is agreeing a different sitting, not editing
            the package - so this is kept to the order it is agreed for and
            never written back to the package master. */}
        {session.chargedByPax && session.packageId && !session.menuUnmappable ? (
          <Box sx={{ mb: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(session.customizePackage)}
                  onChange={(event) =>
                    props.updateSession(session.key, { customizePackage: event.target.checked })
                  }
                />
              }
              label={<Typography variant="body2">Customize this package</Typography>}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Swap, drop or add dishes apart from the package, and agree a rate for them. Nothing here
              changes the package itself.
            </Typography>
            {session.customizePackage ? (
              <TextField
                size="small"
                type="number"
                label="Rate per head (Rs.)"
                value={session.customRate ?? ''}
                onChange={(event) =>
                  props.updateSession(session.key, { customRate: event.target.value })
                }
                placeholder={String(
                  forThisSitting.find((candidate) => candidate.id === session.packageId)
                    ?.basePricePerPerson ?? '',
                )}
                helperText="Blank keeps the package's own rate"
                sx={{ mt: 1, width: 220 }}
              />
            ) : null}
          </Box>
        ) : null}

        {session.menuUnmappable ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            <Typography variant="body2" gutterBottom>
              {session.packageId
                ? 'A dish on this sitting could not be matched to a choice on the package chosen here.'
                : "What's on this sitting already isn't linked to a package here - ordinary for an order converted from a quotation."}{' '}
              To keep from guessing at what is actually being cooked, saving leaves this sitting's menu
              exactly as it is; change a dish on the order's own menu list instead. Everything else
              here - the hall, the time, the pax - still saves normally.
            </Typography>
            {session.existingDishes && session.existingDishes.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {session.existingDishes.map((name, index) => (
                  <Chip key={index} size="small" variant="outlined" label={name} />
                ))}
              </Stack>
            ) : null}
          </Alert>
        ) : null}

        {session.chargedByPax && session.packageId && !session.menuUnmappable
          ? rules.map((rule) => {
              const chosen = session.selections[rule.id] ?? []
              const under = chosen.length < rule.minSelections
              const over = chosen.length > rule.maxSelections
              // The package says how many this item allows, and that is the
              // ceiling: once it is reached the rest are closed off rather than
              // left tickable for the preparer to be told off about later.
              const full = chosen.length >= rule.maxSelections
              return (
                <Box key={rule.id} sx={{ mb: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">
                      <strong>Item {rule.itemNumber}</strong> · {rule.itemLabel}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={under ? 'warning' : over && !rule.allowExtra ? 'error' : 'default'}
                      label={
                        rule.minSelections === rule.maxSelections
                          ? `${chosen.length} of ${rule.minSelections}`
                          : `${chosen.length} of ${rule.minSelections}–${rule.maxSelections}`
                      }
                    />
                  </Stack>
                  <Grid container>
                    {rule.choices.map((choice) => {
                      const picked = chosen.includes(choice.menuItemId)
                      return (
                        <Grid item xs={12} sm={6} key={`${rule.id}-${choice.menuItemId}`}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <FormControlLabel
                              sx={{ mr: 0, flexShrink: 0 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={picked}
                                  // Mandatory dishes cannot be dropped, and once
                                  // the item is full the unpicked ones cannot be
                                  // added - unless this sitting is being
                                  // customized, where both limits are the
                                  // office's own to set aside.
                                  disabled={
                                    !session.customizePackage
                                    && (choice.mandatory || (full && !picked))
                                  }
                                  onChange={(event) =>
                                    toggleChoice(rule.id, choice.menuItemId, event.target.checked)
                                  }
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  {choice.name}
                                  {choice.mandatory ? ' (always)' : ''}
                                </Typography>
                              }
                            />
                            {/* Live cooking is offered only where the catalogue
                                says the dish can be done at a counter, and only
                                once it has actually been chosen. */}
                            {picked && choice.liveCounter ? (
                              <FormControlLabel
                                sx={{ mr: 0 }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={
                                      session.liveCooking[`${rule.id}:${choice.menuItemId}`] ?? false
                                    }
                                    onChange={(event) =>
                                      setLiveCooking(rule.id, choice.menuItemId, event.target.checked)
                                    }
                                    inputProps={{ 'aria-label': `Cook ${choice.name} live` }}
                                  />
                                }
                                label={<Typography variant="caption">Live</Typography>}
                              />
                            ) : null}
                            {/* A remark belongs to a dish that was actually chosen. */}
                            {picked ? (
                              <TextField
                                size="small"
                                variant="standard"
                                placeholder={choice.remarks || 'Remarks'}
                                value={session.menuRemarks[`${rule.id}:${choice.menuItemId}`] ?? ''}
                                onChange={(event) =>
                                  setRemark(rule.id, choice.menuItemId, event.target.value)
                                }
                                sx={{ flexGrow: 1, minWidth: 0 }}
                                inputProps={{
                                  'aria-label': `Remarks for ${choice.name}`,
                                  maxLength: 200,
                                }}
                              />
                            ) : null}
                          </Stack>
                        </Grid>
                      )
                    })}
                  </Grid>
                  {session.customizePackage ? (
                    <DishPicker
                      label={`Add another dish for Item ${rule.itemNumber}`}
                      chosen={session.customItems?.[rule.id] ?? []}
                      onChange={(dishes) =>
                        props.updateSession(session.key, {
                          customItems: { ...session.customItems, [rule.id]: dishes },
                        })
                      }
                    />
                  ) : null}
                </Box>
              )
            })
          : null}

        {session.customizePackage
          ? extraItemIds.map((id, index) => (
              <Box key={id} sx={{ mb: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    <strong>Item {highestItemNumber + index + 1}</strong>
                  </Typography>
                  <Button size="small" color="error" onClick={() => removeExtraItem(id)}>
                    Remove
                  </Button>
                </Stack>
                <DishPicker
                  label={`Add a dish for Item ${highestItemNumber + index + 1}`}
                  chosen={session.customItems?.[id] ?? []}
                  onChange={(dishes) =>
                    props.updateSession(session.key, {
                      customItems: { ...session.customItems, [id]: dishes },
                    })
                  }
                />
              </Box>
            ))
          : null}
        {session.customizePackage ? (
          <Button size="small" onClick={addExtraItem} sx={{ mb: 1.5 }}>
            + Add another item
          </Button>
        ) : null}

        <SessionItems
          sessionKey={session.key}
          byQuantity={!session.chargedByPax}
          extraItems={props.extraItems}
          setExtraItems={props.setExtraItems}
        />

        {props.issues.length > 0 ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {props.issues.map((issue, index) => (
              <div key={index}>{issue}</div>
            ))}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}


/**
 * What this sitting is charged for by the item rather than by the head.
 *
 * <p>A package or a plate of dishes prices a sitting per guest. Plenty of
 * business is not sold that way at all - ten kilos of sweets, a live counter,
 * hired glassware - and some orders are nothing but that. Each line is a
 * product off the catalogue with a quantity and a rate, and the two multiplied
 * are the amount, whatever the head count is.</p>
 *
 * <p>It sits inside the sitting because that is what it belongs to: a counter
 * at the reception is not a counter at the next day's lunch, and the kitchen
 * has to know which is which.</p>
 */
function SessionItems(props: {
  sessionKey: string
  /** The sitting is sold by the quantity rather than by the head. */
  byQuantity: boolean
  extraItems: ExtraItem[]
  setExtraItems: (items: ExtraItem[]) => void
}) {
  const { extraItems, setExtraItems, sessionKey } = props

  // The same entry the counter uses on a bill, over this sitting's own lines.
  // Held flat across every sitting, so writing back keeps the others as they
  // were rather than replacing the lot.
  const mine = extraItems
    .filter((item) => item.sessionKey === sessionKey)
    .map((item) => ({ ...item }) as BillLine)

  const writeBack = (lines: BillLine[]) =>
    setExtraItems([
      ...extraItems.filter((item) => item.sessionKey !== sessionKey),
      ...lines.map((line) => ({ ...line, sessionKey }) as ExtraItem),
    ])

  return (
    <BillLines
      lines={mine}
      setLines={writeBack}
      title={props.byQuantity ? 'What is sold' : 'Extra Items'}
      helper={
        props.byQuantity
          ? 'Every dish for this sitting, each with its own quantity and rate.'
          : 'Charged by the quantity, not by the head — sweets by the kilo, a live counter, hired glassware.'
      }
    />
  )
}

// --------------------------------------------------------------- step four

function ChargesStep(props: {
  discountMode: 'PERCENT' | 'AMOUNT'
  setDiscountMode: (mode: 'PERCENT' | 'AMOUNT') => void
  discountValue: string
  setDiscountValue: (value: string) => void
  discountRecommendedBy: string
  setDiscountRecommendedBy: (value: string) => void
  discountReason: string
  setDiscountReason: (value: string) => void
  validUntil: string
  setValidUntil: (value: string) => void
  paymentTerms: string
  setPaymentTerms: (value: string) => void
  notes: string
  setNotes: (value: string) => void
  asOrder: boolean
  orderedByName: string
  setOrderedByName: (value: string) => void
  isAdmin: boolean
  orderNumber: string
  setOrderNumber: (value: string) => void
  /** Revising an order: it has neither a validity date nor payment terms
      of its own, whatever the quotation it came from once had. */
  hideQuotationFields?: boolean
}) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={6} md={4}>
        <TextField
          select
          fullWidth
          label="Discount as"
          value={props.discountMode}
          onChange={(event) => props.setDiscountMode(event.target.value as 'PERCENT' | 'AMOUNT')}
        >
          <MenuItem value="PERCENT">Percent</MenuItem>
          <MenuItem value="AMOUNT">Flat amount</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={6} md={4}>
        {/* The rates the business gives are offered under the box, but only
            where the discount is a percentage: a flat amount off is not one of
            them and a list of percentages beside it would only mislead. */}
        {props.discountMode === 'PERCENT' ? (
          <DiscountField
            value={props.discountValue}
            onChange={props.setDiscountValue}
            onReason={(reason) => {
              if (!props.discountReason.trim()) props.setDiscountReason(reason)
            }}
            helperText="Applied once, across every session"
          />
        ) : (
          <TextField
            fullWidth
            type="number"
            label="Discount amount"
            value={props.discountValue}
            onChange={(event) => props.setDiscountValue(event.target.value)}
            helperText="Applied once, across every session"
          />
        )}
      </Grid>
      {/* A discount is rarely the preparer's own idea. Asked for only once
          there is one, so an undiscounted quotation is not made longer. */}
      {Number(props.discountValue || 0) > 0 ? (
        <>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="Recommended by"
              value={props.discountRecommendedBy}
              onChange={(event) => props.setDiscountRecommendedBy(event.target.value)}
              helperText="Whose word this was given on"
              inputProps={{ maxLength: 120 }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Why"
              value={props.discountReason}
              onChange={(event) => props.setDiscountReason(event.target.value)}
              helperText="Optional, but it is what the approver reads"
              inputProps={{ maxLength: 300 }}
            />
          </Grid>
        </>
      ) : null}
      {props.hideQuotationFields ? null : (
        <>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Valid until"
              value={props.validUntil}
              onChange={(event) => props.setValidUntil(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Payment terms"
              value={props.paymentTerms}
              onChange={(event) => props.setPaymentTerms(event.target.value)}
              placeholder="25% advance, 25% before menu freeze, balance before the event"
            />
          </Grid>
        </>
      )}
      {/* Only on an order taken over the counter: there is a person who
          placed it, and no acceptance record to name them anywhere else. */}
      {props.asOrder ? (
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Ordered by"
            value={props.orderedByName}
            onChange={(event) => props.setOrderedByName(event.target.value)}
            helperText="Who placed it, as it goes on the record"
          />
        </Grid>
      ) : null}
      {/* Old data, typed in as it stood - not an order taken today, which
          takes its number from this company's own run and no other. */}
      {props.asOrder && props.isAdmin ? (
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Old data — order number"
            value={props.orderNumber}
            onChange={(event) => props.setOrderNumber(event.target.value)}
            helperText="Exactly as it stood before — leave blank for a new order, this company's own numbering carries on untouched"
          />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label={props.asOrder || props.hideQuotationFields ? 'Notes' : 'Notes for the customer'}
          value={props.notes}
          onChange={(event) => props.setNotes(event.target.value)}
        />
      </Grid>
    </Grid>
  )
}

// ------------------------------------------------------------- price panel

function PricePanel({
  quote,
  pending,
  error,
  revisingOrder = false,
  sessions,
  halls,
}: {
  quote: QuotationPreview | null
  pending: boolean
  error: unknown
  /** Shown only before the first preview has come back for an order being
      revised - the fallback text reads differently, since there is no
      empty sitting list here the way there is for a quotation never
      started. */
  revisingOrder?: boolean
  /** Which hall each sitting is in, so the hall it charges for can be shown. */
  sessions: SessionDraft[]
  halls?: Hall[]
}) {
  // Every hall a sitting is actually in, once each - so a wedding that moves
  // from one hall to another for its reception is not charged the first
  // hall's cleaning twice over, and is not charged the second hall's at all
  // if nobody picked it yet.
  const hallsInUse = [...new Set(sessions.map((s) => s.hallId).filter(Boolean))]
    .map((id) => (halls ?? []).find((hall) => hall.id === id))
    .filter((hall): hall is Hall => Boolean(hall))
  const hallCharges = hallsInUse.flatMap((hall) => {
    const rows: { label: string; amount: number }[] = []
    const amenities = (hall.cleaningCharge ?? 0) + (hall.tentativeEbCharge ?? 0)
    if (amenities > 0) {
      rows.push({ label: `${hall.name}: amenities (cleaning, electricity estimate)`, amount: amenities })
    }
    if (hall.ebRatePerUnit > 0) {
      rows.push({ label: `${hall.name}: electricity per unit (meter)`, amount: hall.ebRatePerUnit })
    }
    return rows
  })
  // The venue's own charges are an ordinary service, not catering - taxed at
  // eighteen, the same rate the confirmation states them at. The per-unit
  // meter rate above is not itself a charge, so nothing here taxes it.
  const amenitiesTotal = hallsInUse.reduce(
    (sum, hall) => sum + (hall.cleaningCharge ?? 0) + (hall.tentativeEbCharge ?? 0),
    0,
  )
  const amenitiesTax = amenitiesTotal * 0.18
  return (
    <Card sx={{ position: 'sticky', top: 16 }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Price
        </Typography>
        <ErrorBanner error={error} />

        {!quote ? (
          <Typography variant="body2" color="text.secondary">
            {revisingOrder
              ? 'Every session, its package and its rate stay as they are unless changed here. Saving prices the order fresh.'
              : 'Add a session with a head count to see the price.'}
          </Typography>
        ) : (
          <>
            {pending ? (
              <Typography variant="caption" color="text.secondary">
                repricing…
              </Typography>
            ) : null}

            <Table size="small">
              <TableBody>
                {quote.sessions.map((session) => {
                  // Everything this screen works its own arithmetic from -
                  // sessionAmount included - is net of tax, the same way the
                  // quotation's own Subtotal below it is: correct for adding
                  // the tax once at the foot, but not what pax times the
                  // per-head rate shown (already converted back to what the
                  // order will actually charge) comes to. Grossed up the same
                  // way, so a row reads as what this sitting is actually
                  // billed for, not a mid-calculation figure.
                  const grossUp = (net: number) => net * (1 + (quote.taxRatePercent || 0) / 100)
                  // Hall rental rides on the sitting's own amount with no
                  // breakdown of its own anywhere else on this screen, so it
                  // is named here, in full, or the gap between pax × rate and
                  // the row's amount has nothing explaining it at all. Extra
                  // items are the opposite case - named here only in words,
                  // never with a second figure of their own - because they
                  // already have a complete one, to the paisa, in their own
                  // Extra items table further down; stating it again here,
                  // rounded slightly differently by different code, is what
                  // read as GST worked out twice for the one line.
                  const extraItems = session.extraItemAmount + session.alaCarteAmount
                  return (
                    <TableRow key={`${session.mealPeriod}-${session.sessionDate}`}>
                      <TableCell sx={{ border: 0, py: 0.5 }}>
                        <Typography variant="body2">{session.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {session.billedPax} billed
                          {session.alaCarte
                            ? ' · à la carte'
                            : // Rounded to the nearest rupee, pax times the rate
                              // shown stops landing on the row's own amount
                              // (itself exact) whenever the per-head figure
                              // does not divide evenly - which read as a
                              // second, silent tax rather than the display's
                              // own rounding. Shown to the paisa instead, the
                              // same precision the amount beside it already
                              // keeps.
                              ` · ${formatMoney(session.grossPricePerPerson, true)}/head`}
                          {!session.alaCarte && session.hallRentalAmount > 0
                            ? ` + ${formatMoney(grossUp(session.hallRentalAmount))} hall rental`
                            : ''}
                          {!session.alaCarte && extraItems > 0 ? ' + extra items (below)' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, py: 0.5 }}>
                        <Typography variant="body2">
                          {formatMoney(grossUp(session.sessionAmount), true)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}

                <Row label="Subtotal (before tax)" value={quote.subtotal} />
                {quote.discountAmount > 0 && (
                  <Row
                    label={`Discount${
                      quote.subtotal > 0
                        ? ` (${((quote.discountAmount / quote.subtotal) * 100).toFixed(2)}%)`
                        : ''
                    }`}
                    value={-quote.discountAmount}
                  />
                )}
                <Row label="Taxable" value={quote.taxableAmount} />
                <Row label={`GST ${quote.taxRatePercent}%`} value={quote.taxAmount} />
                {quote.roundOff !== 0 && <Row label="Round off" value={quote.roundOff} />}
                <Row label="Total" value={quote.total} bold />
              </TableBody>
            </Table>

            {hallCharges.length > 0 ? (
              <>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  Hall charges, extra to the above
                </Typography>
                <Table size="small">
                  <TableBody>
                    {hallCharges.map((row) => (
                      <Row key={row.label} label={row.label} value={row.amount} />
                    ))}
                    {amenitiesTax > 0 ? (
                      <>
                        <Row label="GST 18%" value={amenitiesTax} />
                        <Row label="Total" value={amenitiesTotal + amenitiesTax} bold />
                      </>
                    ) : null}
                  </TableBody>
                </Table>
              </>
            ) : null}

            {!quote.valid ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {quote.issues.map((issue, index) => (
                  <div key={index}>{issue}</div>
                ))}
              </Alert>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function Row({ label, value, bold }: { label: string; value: number; bold?: boolean }) {
  return (
    <TableRow>
      <TableCell sx={{ border: 0, py: 0.5, pt: bold ? 1 : 0.5 }}>
        <Typography variant={bold ? 'subtitle2' : 'body2'}>{label}</Typography>
      </TableCell>
      <TableCell align="right" sx={{ border: 0, py: 0.5 }}>
        <Typography variant={bold ? 'subtitle2' : 'body2'}>{formatMoney(value, true)}</Typography>
      </TableCell>
    </TableRow>
  )
}
